<template>
	<c-dialog ref="saveResumeDialog" :visible="open" class="cog-save-resume-dialog" @opened="handleDialogOpened" v-on="$listeners">
		<template #title>
			<c-row>
				<div class="cog-content cog-col cog-col--24">
					<h2 class="cog-heading">{{ resourceData('save-and-resume-message') }}</h2>
					<p>{{ saveMessage }}</p>
				</div>
			</c-row>
		</template>

		<!-- Do not show the link if save and resume requires authentication but the user is not logged in (link is not populated)-->
		<c-row v-if="!hideLink">
			<c-field field-id="cog-save-resume-link" :label="resourceData('save-and-resume-copy-link-label')">
				<c-input id="cog-save-resume-link" :value="link" @click.native="$event.target.select()" />
			</c-field>
		</c-row>

		<c-row>
			<c-field>
				<label :for="!readOnly ? 'cog-save-and-resume-email' : undefined">
					<span>{{ resourceData('save-and-resume-email-label') }}</span>
					<span v-if="!readOnly" class="cog-asterisk">*</span>
				</label>
				<c-row>
					<c-input id="cog-save-and-resume-email" type="email" class="cog-input--long" required="true" :value="email" :readonly="readOnly" @input="handleEmailInput" @change="handleEmailChange" @keydown.enter.native.prevent="sendEmail" />
					<div class="cog-input">
						<c-status-button :disabled="disabled" :status="emailStatus" :duration="4000" class="cog-save-resume__button cog-button--primary" @click.native="sendEmail">{{ resourceData('send-button-text') }}</c-status-button>
					</div>
					<transition name="cog-error-message">
						<div v-if="emailError && showEmailError" class="cog-error-message cog-error-message--container-footer">{{ emailError }}</div>
					</transition>
				</c-row>
			</c-field>
		</c-row>
	</c-dialog>
</template>

<script lang="ts">
	import { computed, inject, ref, watch } from 'vue';
	import CDialog from './Dialog.vue';
	import CField from './FieldNoSource.vue';
	import CInput from './Input.vue';
	import CStatusButton from './StatusButton.vue';
	import CRow from './Row.vue';
	import { FormStatusProp } from 'src/mixins/form-status';
	import { formatAddresses, reformatEmail } from 'src/util/email';
	import type { VueModel } from '@cognitoforms/vuemodel/@types/vue-model';

	export default {
		name: 'CSaveResumeDialog',
		components: {
			CDialog,
			CInput,
			CStatusButton,
			CField,
			CRow
		},
		props: {
			open: {
				type: Boolean,
				required: true
			},
			link: {
				type: String,
				default: null
			},
			emailAddress: {
				type: String,
				default: null
			},
			emailAddressReadOnly: {
				type: Boolean,
				default: false
			},
			emailStatus: FormStatusProp,
			enableSend: {
				type: Boolean,
				default: true
			},
			message: {
				type: String,
				default: ''
			},
			requireAuthentication: {
				type: Boolean,
				default: false
			},
			isLoggedIn: {
				type: Boolean,
				default: false
			}
		},
		emits: ['send-email'],
		setup(props, { emit }) {
			const email = ref<string>(props.emailAddress);
			const emailError = ref<string>(null);
			const showEmailError = ref<boolean>(false);
			const resourceData = inject<VueModel['getResource']>('$resource');

			const hideLink = computed(() => {
				return !props.link;
			});

			const readOnly = computed(() => {
				return (props.requireAuthentication && props.isLoggedIn) || props.emailAddressReadOnly;
			});

			const saveMessage = computed(() => {
				if (hideLink.value)
					return resourceData('authenticated-save-and-resume-email-dialog');
				return props.message ? props.message : resourceData('save-and-resume-no-expiration-message');
			});

			watch(() => props.emailAddress, (val) => {
				email.value = val;
			});

			const handleEmailInput = (text) => {
				// Store the new email value
				email.value = text;
			};

			const isValidEmail = () => {
				const formattedEmails = formatAddresses(email.value);

				// If there is no email, then mark it as required
				if (!email.value) {
					emailError.value = resourceData('required', { property: 'Email' });
					showEmailError.value = true;
					return false;
				}
				// If the email is improperly formatted, then show a format error
				else if (!formattedEmails) {
					emailError.value = resourceData('string-format', { property: 'Email', formatDescription: 'name@domain.com' }) || 'Email must be formatted as name@domain.com.';
					showEmailError.value = true;
					return false;
				}
				// Valid
				else {
					emailError.value = null;
					showEmailError.value = false;
					return true;
				}
			};

			const disabled = computed(() => {
				return !email.value || !props.enableSend || (emailError.value && showEmailError.value);
			});

			const handleEmailChange = (text) => {
				// Store the new email value
				email.value = text;
				// Check whether the email is valid
				if (isValidEmail()) {
					// Reformat the email if it is valid
					email.value = reformatEmail(text);
				}
			};

			const sendEmail = () => {
				// If the email is valid, then raise the event
				if (isValidEmail()) {
					emit('send-email', email.value);
				}
			};

			const handleDialogOpened = (el) => {
				if (!readOnly.value)
					el.querySelector('input[type=email]').focus();
			};

			return {
				email,
				emailError,
				showEmailError,
				saveMessage,
				hideLink,
				readOnly,
				disabled,
				resourceData,
				handleEmailInput,
				handleEmailChange,
				sendEmail,
				handleDialogOpened
			};
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {
	// Get button vertically centered with email input.
	// Using a more traditional method like align-items: center on the row would work, but
	// the button (if bigger), would push the email input away from the label. With very big buttons, this looks dumb.
	.cog-cognito--styled .cog-save-resume__button {
		margin-top: calc((var(--button-primary__font-size) - var(--input__font-size, 1em)) * -.5);
	}

	&.cog-iframe-embed .cog-save-resume-dialog .el-dialog {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
	}
}
</style>